import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import HighConfidenceResults from './high_confidence_results.js';
import LowConfidenceResults from './low_confidence_result.js';
import { initialFiltersState, newsFeedFilterReducer as filterReducer } from '../../admin_views/filters/ProspectBasedFilters/reducer/newsFeedFilterReducer.js';
import FiltersIcon from '../../../assets/FiltersIcon.svg'
import CalendarIcon from '../../../assets/CalendarIcon.svg'
import GreenDot from '../../../assets/GreenDot.svg'
import { initialNewsFeedState, NewsFeedReducer } from './reducers/newsFeedReducer.js';
import { ProspectBasedDateFilter, ProspectBasedFilters } from '../../admin_views/filters/ProspectBasedFilters/filter/ProspectBasedFilters.js';
import ConditionalRender from '../../../ConditionalRender.js';
import { NewsFeedHighConfidenceTabs } from '../../utils/AllFilterTabs.js';
import { apiAgent } from "../../../axiosConfig.js";
import { useSelector } from 'react-redux';
import { NameInitial } from '../../cta_utils/AnNameInitial.js';
import Avatar from '../../../design_system/components/Avatar/Avatar.jsx';
import { isWidthDown } from '../../utils/designUtils.js';
import useWindowSize from '../../custom_hooks/useWindowSize.js';
import { Link } from 'react-router-dom';
import TopProspectImage from "../../../assets/top_prospect.svg";
import { Tooltip } from '../../../design_system/components/ToolTip/ToolTip.jsx';
import { applyUrlFilters, getFilterUrl } from '../../utils/ApplyUrlFilters.js';
import MatchingArticleDateFilters from '../../admin_views/filters/ArticleBasedFilters/filters/matchingArticleDateFilters.jsx';
import AnDropdownGroupFilter from '../../cta_utils/AnDropdownGroupfilter.js';
import { PostFilterOptions, TempPostFilterOptions } from '../../utils/PostFilterOptions.js';
import { indexOfObject } from '../../utils/ArrayUtils.js';
import useCheckUserAccess from '../../custom_hooks/useCheckUserAccess.js';
import { Button } from '../../../design_system/components/Button/Button.jsx';
import { PillTabs } from '../../../design_system/components/Tabs/PillTabs.jsx';
import SecFilingAlert from './announcements/SecFilingAlert.jsx';
import { Tracker } from '../../utils/mixpanel/tracker.js';
import { confidenceTabLabel } from '../../utils/AllLabels.js';
import { canViewFeedType } from '../../../utils/access.js';

const postTypes = [{ label: "All results", key: "all" }, { label: "Only News articles", key: "articles" }, { label: "Only Job updates", key: "Job_Updates" }, { label: "SEC Filings", key: "SEC_Filings" }];

const initFilters = ({ ...initState }) => {
    return applyUrlFilters(initState, []);
}

const fetchInitialState = (initialState) => {
    const paramsString = new URLSearchParams(window.location.search);
    let type = paramsString.get("post_type");
    type = type ? type.replace(/"/g, "").slice(1, -1) : null;
    let post_type = { ...initialState.postType };
    if (type) {
        let idx = indexOfObject(postTypes, "key", type);
        post_type = postTypes[idx];
    }
    return {
        ...initialState,
        postType: post_type
    }
}
const initNewsfeedState = ({ ...initialState }) => {
    return fetchInitialState(initialState);
}

export const NewsFeed = ({
    lowConfidenceUrl = '/aggregates',
    highConfidenceUrl = '/feed',
    displayFilterSection = true,
    displaySideColumn = true,
    displaySearchBar = false,
    filtersInUrl = true,
    trackAnalytics = false,
    ...props
}) => {
    const [newsFeedState, newsFeedReducer] = useReducer(NewsFeedReducer, initialNewsFeedState, initNewsfeedState)
    const [filtersState, filtersStateReducer] = useReducer(filterReducer, initialFiltersState, initFilters)
    const [teamMembers, setTeamMembers] = useState([]);
    const [topProspects, setTopProspects] = useState([]);
    const current_team = useSelector(state => state.current_team)
    const windowDimensions = useWindowSize();
    const checkUserAccess = useCheckUserAccess();
    const current_user = useSelector(state => state.current_user?.user);
    const canViewJobs = canViewFeedType({ typeKey: "Job_Updates", team: current_team.team, user: current_user });
    const canViewSec = canViewFeedType({ typeKey: "SEC_Filings", team: current_team.team, user: current_user });

    useEffect(() => {
        if (displaySideColumn) {
            getTeamMembers();
            // getTopProspects();
        }
        const paramsString = new URLSearchParams(window.location.search);
        let postsType = paramsString.get("postsType");
        newsFeedReducer({ payload: { confidenceType: postsType || 'high' }, type: 'SET_STATE' })
    }, []);

    const updateParentState = useCallback((payload) => {
        newsFeedReducer({ payload, type: 'SET_STATE' })
    }, [])

    const updateTotalCount = useCallback((high_confidence_results_count, mid_confidence_results_count, low_confidence_results_count) => {
        newsFeedReducer({ payload: { high_confidence_results_count: high_confidence_results_count, mid_confidence_results_count: mid_confidence_results_count, low_confidence_results_count: low_confidence_results_count }, type: 'SET_STATE' })
    }, [])

    const switchConfTab = (tabType) => {
        if (trackAnalytics && newsFeedState.confidenceType !== tabType) Tracker.track("Newsfeed Tab Switched", { from: confidenceTabLabel(newsFeedState.confidenceType), to: confidenceTabLabel(tabType) || tabType });
        if (filtersState.confidenceType != tabType) filtersStateReducer({ payload: { confidenceType: tabType, allFilterTabs: newsFeedState.allFilterTabs }, type: 'RESET_FILTER' })
        newsFeedReducer({ payload: { confidenceType: tabType }, type: 'SET_STATE' })
    }
    const allTopProspectUrl = useMemo(() => { let url = getFilterUrl({ miscellaneous: ["Top Prospect"] }, "/prospects", ["miscellaneous"], { type: "short" }); return url }, []);

    const renderResultTabs = () => {
        const showCount = !isWidthDown(windowDimensions.width, "md")
        const is_mid_enable = current_team.team.displayable_features.display_mid_confidence
        const tabs = [
            { key: 'high', label: 'High Confidence', level: 'high', ...(showCount && newsFeedState.high_confidence_results_count > 0 ? { count: newsFeedState.high_confidence_results_count } : {}) },
            ...(is_mid_enable ? [{ key: 'mid', label: 'Mid Confidence', level: 'mid', ...(showCount && newsFeedState.mid_confidence_results_count > 0 ? { count: newsFeedState.mid_confidence_results_count } : {}) }] : []),
            { key: 'low', label: 'Low Confidence', level: 'low', ...(showCount && newsFeedState.low_confidence_results_count > 0 ? { count: newsFeedState.low_confidence_results_count } : {}) },
        ]
        return (
            <PillTabs
                tabs={tabs}
                activeKey={newsFeedState.confidenceType}
                onChange={switchConfTab}
                fill
                countStyle="badge"
            />
        )
    }

    const getTeamMembers = () => {
        apiAgent({
            method: 'get',
            url: '/team/team_members',
            params: {}
        }).then(
            (response) => {
                setTeamMembers(response.data.team_members);
            },
            (error) => {
            }
        );
    }

    const getTopProspects = () => {
        apiAgent({
            method: 'get',
            url: '/team/top_prospects',
            params: {}
        }).then(
            (response) => {
                setTopProspects(response.data.data);
            },
            (error) => {
            }
        );
    }

    const renderResult = () => {
        if (newsFeedState.confidenceType === 'low') {
            return (
                <LowConfidenceResults
                    fetchUrl={lowConfidenceUrl}
                    displayFilterSection={displayFilterSection}
                    filtersState={filtersState}
                    filtersStateReducer={filtersStateReducer}
                    parentReducer={newsFeedReducer}
                    confidenceType={newsFeedState.confidenceType}
                    trackAnalytics={trackAnalytics}
                    allFilterTabs={newsFeedState.allFilterTabs}
                />
            );
        }
        else if (newsFeedState.confidenceType === 'mid') {
            return (
                <LowConfidenceResults
                    fetchUrl={lowConfidenceUrl}
                    displayFilterSection={displayFilterSection}
                    filtersState={filtersState}
                    filtersStateReducer={filtersStateReducer}
                    parentReducer={newsFeedReducer}
                    confidenceType={newsFeedState.confidenceType}
                    trackAnalytics={trackAnalytics}
                    allFilterTabs={newsFeedState.allFilterTabs}
                />
            );
        }
        else if (newsFeedState.confidenceType === 'high') {
            return (
                <HighConfidenceResults
                    fetchUrl={highConfidenceUrl}
                    filtersState={filtersState}
                    filtersStateReducer={filtersStateReducer}
                    parentReducer={newsFeedReducer}
                    displaySearchBar={displaySearchBar}
                    filtersInUrl={filtersInUrl}
                    newsFeedItems={{ dropDownFilterlist: newsFeedState.dropDownFilterlist, allFilterTabs: newsFeedState.allFilterTabs }}
                    displayFilterSection={displayFilterSection}
                    total_count={newsFeedState.high_confidence_results_count}
                    trackAnalytics={trackAnalytics}
                />
            );
        }
    }

    if (newsFeedState.leadingError) return <div>{newsFeedState.leadingError}</div>

    let toggleMobile = isWidthDown(windowDimensions.width, "md") && canViewJobs
    let show_job_update_filter = newsFeedState.confidenceType === 'high' && canViewJobs
    return (
        <div className="flex items-start flex-wrap mx-auto newsfeed__layout">
            <div className='w-full newsfeed__main-column'>
                {renderResultTabs()}
                {renderResult()}
                {
                    filtersState.popupsList?.showFilterPopup && <ProspectBasedFilters
                        filtersState={filtersState}
                        filtersStateReducer={filtersStateReducer}
                        suggestionUrl='/posts/post_term_search'
                        tempFilterReducer={filterReducer}
                        allFilterTabs={newsFeedState.allFilterTabs}
                        type={newsFeedState.confidenceType}
                    />
                }

            </div>
            <ConditionalRender if={displaySideColumn}>
                <div className="sticky w-full newsfeed__side-column">
                    <ConditionalRender if={canViewSec && newsFeedState.confidenceType === 'high' && filtersState.postType !== 'SEC_Filings'}>
                        <SecFilingAlert tab={"All"} />
                    </ConditionalRender>
                    <ConditionalRender if={checkUserAccess('page_search_space')}>
                        <div className="newsfeed__section-card newsfeed__section-card--hoverable">
                            <div>
                                <div className="body-l-bold newsfeed__section-title">Search News using Keywords</div>
                                <div className='body-s newsfeed__section-hint'>Can't find specific news? Use our keyword search to quickly discover relevant news about your prospects. </div>
                            </div>
                            <Link to='/search' className="no-underline">
                                <Button
                                    Size="Small"
                                    buttonStyle={{ marginTop: '16px', width: "max-content" }}
                                    innerButtonStyle={{ padding: "6px 12px" }}
                                >
                                    Search Now
                                </Button>
                            </Link>
                        </div>
                    </ConditionalRender>

                    <div className="newsfeed__section-card">
                        <div className="newsfeed__section-title flex-header">
                            <div>Team</div>
                            <ConditionalRender if={current_team.team.is_admin}>
                                <a onClick={() => window.dispatchEvent(new CustomEvent('open-settings', { detail: { tab: 'people' } }))} className="body-s-bold link-default cursor-pointer">+Invite</a>
                            </ConditionalRender>
                        </div>
                        {
                            teamMembers && teamMembers.length > 0 && teamMembers.slice(0, 4).map(member => {
                                return (
                                    <div key={member} className="flex justify-between items-start newsfeed__members-container">
                                        <div className="flex gap-s items-center flex-1 newsfeed__members-info newsfeed__members-info--no-pointer">
                                            <Avatar name={member} width={40} textStyle={{ fontSize: 16 }} />
                                            <div className="body-m-bold flex-1 min-w-0 newsfeed__members-name">
                                                {member}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                        <ConditionalRender if={checkUserAccess("view_all_button")}>
                            {teamMembers.length > 5 && <a className="link-default body-s-bold flex justify-center cursor-pointer" onClick={() => window.dispatchEvent(new CustomEvent('open-settings', { detail: { tab: 'people' } }))}> View all</a>}
                        </ConditionalRender>
                    </div>
                    {
                        topProspects && topProspects.length > 0 && (
                            <div className="newsfeed__section-card">
                                <div className="body-l-bold newsfeed__section-title">
                                    <div className='flex-between'>
                                        <div className="body-s-semibold newsfeed__item-subtitle"><img src={TopProspectImage} className="newsfeed__section-icon" />Top prospects this week</div>
                                        {/* <a href={allTopProspectUrl} target="_blank" className='text_link'>View all</a> */}
                                    </div>
                                    <div className="body-s-semibold newsfeed__item-subtitle">Prospect featured in news articles higher than their weekly average</div>
                                </div>

                                <div className="newsfeed__divider"></div>
                                {
                                    topProspects.map(prospect => {
                                        const name = prospect.name || prospect.company;
                                        const designation = prospect.designation;
                                        return (
                                            <div key={prospect.id} className="flex items-center w-full newsfeed__prospect-container newsfeed__prospect-container--list-item">
                                                <div className="newsfeed__prospect-info">
                                                    <NameInitial name={name} width={40} textStyle={{ fontSize: 16 }}>
                                                    </NameInitial>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="body-m-bold newsfeed__item-title newsfeed__item-title--with-icon">
                                                            {name}
                                                            <Tooltip
                                                                tooltipText={prospect.top_prospect_stat}
                                                                placement='bottom'
                                                            >
                                                                <img src={TopProspectImage} className="newsfeed__top-prospect-icon" />
                                                            </Tooltip>
                                                        </div>
                                                        {designation && (
                                                            <div className="body-s-semibold newsfeed__item-subtitle truncate">
                                                                {designation}
                                                            </div>)}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                                <Link to={allTopProspectUrl} className='link-default body-s-bold newsfeed__link centered'>View all</Link>
                            </div>)
                    }
                </div>
            </ConditionalRender>
        </div>
    )
}

export default NewsFeed;
